import { useCallback, useMemo, useState } from 'react';
import { ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';

/**
 * @param {object} p
 * @param {(key: string) => string} p.t
 * @param {boolean} p.isFullscreen
 * @param {(next: 'split'|'fullscreen') => void} [p.onLayoutChange]
 * @param {object} p.messageSearch
 * @param {() => void} p.closeConversationSearch
 * @param {(id: string) => void|Promise<void>} p.selectConversation
 */
export function useAssistantHistoryNav({
  t,
  isFullscreen,
  onLayoutChange,
  messageSearch,
  closeConversationSearch,
  selectConversation,
}) {
  const [historyOpen, setHistoryOpen] = useState(false);

  const handleBackFromHistory = useCallback(() => setHistoryOpen(false), []);

  const handleSelectConversationFromHistory = useCallback(
    (id) => {
      selectConversation(id);
      setHistoryOpen(false);
    },
    [selectConversation],
  );

  const handleOpenHistory = useCallback(() => {
    messageSearch.handleCloseSearch();
    closeConversationSearch();
    setHistoryOpen(true);
  }, [messageSearch, closeConversationSearch]);

  const fullscreenBtn = useMemo(
    () => (
      <button
        type="button"
        onClick={() => onLayoutChange?.(isFullscreen ? 'split' : 'fullscreen')}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content"
        aria-label={isFullscreen ? t('chatPanel.restore') : t('chatPanel.maximize')}
        title={isFullscreen ? t('chatPanel.restore') : t('chatPanel.maximize')}
      >
        {isFullscreen ? <Minimize2 size={16} aria-hidden /> : <Maximize2 size={16} aria-hidden />}
      </button>
    ),
    [isFullscreen, onLayoutChange, t],
  );

  const historyGroupLabels = useMemo(
    () => ({
      today: t('chatPanel.historyGroupToday'),
      yesterday: t('chatPanel.historyGroupYesterday'),
      week: t('chatPanel.historyGroupWeek'),
      month: t('chatPanel.historyGroupMonth'),
      quarter: t('chatPanel.historyGroupQuarter'),
      older: t('chatPanel.historyGroupOlder'),
    }),
    [t],
  );

  const historyTopNavLeft = useMemo(
    () => (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <button
          type="button"
          onClick={handleBackFromHistory}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content"
          aria-label={t('chatPanel.historyBack')}
          title={t('chatPanel.historyBack')}
        >
          <ArrowLeft size={18} aria-hidden />
        </button>
        <h2 className="min-w-0 truncate text-base font-semibold text-content">
          {t('chatPanel.conversationHistoryTitle')}
        </h2>
      </div>
    ),
    [handleBackFromHistory, t],
  );

  const historyTopNavActions = useMemo(
    () => (
      <div className="flex items-center gap-0.5">{fullscreenBtn}</div>
    ),
    [fullscreenBtn],
  );

  return {
    historyOpen,
    handleOpenHistory,
    handleSelectConversationFromHistory,
    historyGroupLabels,
    historyTopNavLeft,
    historyTopNavActions,
    fullscreenBtn,
  };
}
